import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, AlertCircle, Mail, Phone, Package, Home, RefreshCw, Printer } from 'lucide-react';
import axios from 'axios';

const HelmetPaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [verificationAttempts, setVerificationAttempts] = useState(0);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const orderId = params.get('order_id');

        console.log('🔍 Helmet Payment Success Page - Order ID:', orderId);

        if (!orderId) {
          setStatus('error');
          setErrorMessage('No order ID found in the URL');
          return;
        }

        const apiUrl = 'https://auth.kgvl.co.in';
        
        console.log(`🔄 Verification attempt ${verificationAttempts + 1}`);
        
        // ✅ SIMPLIFIED: Just send orderId - backend will handle everything
        const verifyResponse = await axios.post(`${apiUrl}/api/helmet-orders/verify`, {
          orderId: orderId
        });

        console.log('✅ Helmet verify response:', verifyResponse.data);

        if (verifyResponse.data.success) {
          const order = verifyResponse.data.order;
          
          // Check if payment is still pending
          if (order.paymentStatus === 'PENDING') {
            console.log('⏳ Payment still pending, retrying...');
            
            // Retry up to 8 times with 3 second intervals
            if (verificationAttempts < 8) {
              setVerificationAttempts(prev => prev + 1);
              setTimeout(() => verifyPayment(), 3000);
              return;
            }
            
            // After max attempts, show pending status
            setStatus('pending');
            setOrderDetails({
              orderId: order.orderId,
              amount: order.amount,
              customerName: order.customerName,
              customerEmail: order.customerEmail,
              customerPhone: order.customerPhone,
              helmetColor: order.helmetColor,
              quantity: order.quantity,
              address: order.address,
              timestamp: new Date(order.transactionDate).toLocaleString(),
              paymentStatus: 'PENDING'
            });
            return;
          }
          
          // Payment successful
          setStatus('success');
          setOrderDetails({
            orderId: order.orderId,
            paymentId: order.transactionId,
            amount: order.amount,
            customerName: order.customerName,
            customerEmail: order.customerEmail,
            customerPhone: order.customerPhone,
            helmetColor: order.helmetColor,
            quantity: order.quantity,
            address: order.address,
            message: 'Your helmet order has been confirmed!',
            timestamp: new Date(order.transactionDate).toLocaleString(),
            paymentStatus: order.paymentStatus
          });

          // Clean up any storage
          localStorage.removeItem('pendingHelmetOrderData');
          sessionStorage.removeItem('helmetOrderData');
          
          // Clear URL hash
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
          
        } else {
          // Retry on failure
          if (verificationAttempts < 5) {
            console.log(`⏳ Verification failed, retrying (${verificationAttempts + 1}/5)...`);
            setVerificationAttempts(prev => prev + 1);
            setTimeout(() => verifyPayment(), 3000);
            return;
          }
          
          setStatus('error');
          setErrorMessage(verifyResponse.data.message || 'Payment verification failed');
        }
      } catch (error) {
        console.error('❌ Payment verification error:', error);
        
        // Retry on error
        if (verificationAttempts < 5) {
          console.log(`⏳ Error occurred, retrying (${verificationAttempts + 1}/5)...`);
          setVerificationAttempts(prev => prev + 1);
          setTimeout(() => verifyPayment(), 3000);
          return;
        }
        
        setStatus('error');
        setErrorMessage(error.response?.data?.message || 'Payment verification failed. Please contact support with your order ID.');
      }
    };

    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (status === 'loading') {
        setStatus('error');
        setErrorMessage('Verification timeout. Please check your email or contact support with your order ID.');
      }
    }, 40000); // 40 seconds timeout

    verifyPayment();

    return () => clearTimeout(timeoutId);
  }, [location, verificationAttempts, status]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-12 max-w-md w-full text-center">
          <Loader2 className="w-20 h-20 text-green-600 animate-spin mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-800 mb-3">Verifying Your Payment</h2>
          <p className="text-gray-600 mb-2">Please wait while we confirm your helmet order</p>
          <p className="text-sm text-gray-500">Attempt {verificationAttempts + 1} of 8</p>
          <p className="text-xs text-gray-400 mt-4">This may take up to 30 seconds...</p>
        </div>
      </div>
    );
  }

  // Pending state
  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-yellow-100 rounded-full mb-4">
              <AlertCircle className="w-12 h-12 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Processing</h1>
            <p className="text-lg text-gray-600">Your order has been received and is being processed</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
            <h3 className="font-bold text-gray-800 mb-2">Order ID: {orderDetails?.orderId}</h3>
            <p className="text-sm text-gray-700">
              We've received your order and payment confirmation is in progress. 
              You'll receive an email at <strong>{orderDetails?.customerEmail}</strong> once confirmed.
            </p>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setStatus('loading');
                setVerificationAttempts(0);
              }}
              className="w-full bg-yellow-600 text-white px-6 py-4 rounded-lg hover:bg-yellow-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Check Status Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Go to Homepage
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              If you don't receive confirmation within 24 hours, please contact us at{' '}
              <a href="mailto:team@kgvl.co.in" className="text-yellow-600 hover:underline font-medium">
                team@kgvl.co.in
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-red-100 rounded-full mb-4">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Payment Verification Failed</h1>
            <p className="text-lg text-gray-600">{errorMessage}</p>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-semibold mb-1">Important:</p>
                <p className="text-sm text-red-800">
                  If you were charged, please contact our support team for assistance.
                  Check your email for payment confirmation.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => {
                setStatus('loading');
                setVerificationAttempts(0);
              }}
              className="w-full bg-red-600 text-white px-6 py-4 rounded-lg hover:bg-red-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Try Verification Again
            </button>
            <button
              onClick={() => navigate('/order-helmet')}
              className="w-full border-2 border-red-600 text-red-600 px-6 py-4 rounded-lg hover:bg-red-50 transition-all font-semibold"
            >
              Place New Order
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold"
            >
              Go to Homepage
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-600">
              Need help? Email us at{' '}
              <a href="mailto:team@kgvl.co.in" className="text-red-600 hover:underline font-medium">
                team@kgvl.co.in
              </a>
              {' '}or call{' '}
              <a href="tel:+919661829844" className="text-red-600 hover:underline font-medium">
                +91 966-182-9844
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6 text-center">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-green-100 rounded-full mb-6">
            <CheckCircle className="w-16 h-16 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Order Confirmed!</h1>
          <p className="text-xl text-gray-600">Your helmet order has been successfully placed</p>
        </div>

        {/* Order Information Grid */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Order Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
              <Package className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Order ID</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails?.orderId}</p>
              </div>
              {orderDetails?.paymentId && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment ID</p>
                  <p className="text-base font-semibold text-gray-900">{orderDetails.paymentId}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                <p className="text-2xl font-bold text-green-600">
                  ₹{orderDetails?.amount?.toLocaleString('en-IN')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Date & Time</p>
                <p className="text-base font-medium text-gray-900">{orderDetails?.timestamp}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Payment Status</p>
                <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                  {orderDetails?.paymentStatus}
                </span>
              </div>
            </div>
          </div>

          {/* Customer & Product Details */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200">
              <Mail className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Customer Name</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails?.customerName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Email</p>
                <p className="text-base font-medium text-gray-900 break-all">{orderDetails?.customerEmail}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Phone</p>
                <p className="text-base font-medium text-gray-900">{orderDetails?.customerPhone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Helmet Color</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails?.helmetColor}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Quantity</p>
                <p className="text-base font-semibold text-gray-900">{orderDetails?.quantity}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Delivery Address</p>
                <p className="text-base font-medium text-gray-900">{orderDetails?.address}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-lg p-6 mb-6 text-white">
          <h2 className="text-2xl font-bold mb-6">What Happens Next</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                  1
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Confirmation Email</h3>
                <p className="text-green-100 text-sm">
                  We've sent a confirmation email to {orderDetails?.customerEmail} with all order details.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                  2
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Processing</h3>
                <p className="text-green-100 text-sm">
                  Your order is being processed and will be dispatched within 2-3 business days.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center font-bold text-lg">
                  3
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-2">Delivery</h3>
                <p className="text-green-100 text-sm">
                  Track your shipment and expect delivery within 5-7 business days.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-green-600 text-white px-6 py-4 rounded-lg hover:bg-green-700 transition-all font-semibold shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <Home className="w-5 h-5" />
            Homepage
          </button>
          <button
            onClick={() => navigate('/order-helmet')}
            className="border-2 border-green-600 text-green-600 px-6 py-4 rounded-lg hover:bg-green-50 transition-all font-semibold flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-5 h-5" />
            Order Another
          </button>
          <button
            onClick={() => window.print()}
            className="border-2 border-gray-300 text-gray-700 px-6 py-4 rounded-lg hover:bg-gray-50 transition-all font-semibold flex items-center justify-center gap-2"
          >
            <Printer className="w-5 h-5" />
            Print Receipt
          </button>
        </div>

        {/* Support Info */}
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="font-semibold text-gray-900 mb-3">Have Questions About Your Order?</h3>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              <a href="mailto:team@kgvl.co.in" className="hover:text-green-600 transition-colors">
                team@kgvl.co.in
              </a>
            </div>
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4" />
              <a href="tel:+919661829844" className="hover:text-green-600 transition-colors">
                +91 966-182-9844
              </a>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">Mon-Sat: 9AM-6PM</p>
        </div>
      </div>
    </div>
  );
};

export default HelmetPaymentSuccess;